import { useCallback, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { ErrorsNavigationContextProvider } from 'contexts/ErrorsNavigationContext/ErrorsNavigationContextProvider';
import { useInitWebEDIStore } from 'services/webedi/webedi';
import { WebEDIErrorsServiceSingleton } from 'services/webediErrors';
import { WebEDIError } from 'services/webediErrors/types';
import { useHistoryStore } from 'stores/history/history';
import { useWebEDIStore } from 'stores/webedi/webedi';
import { routeToTransactionPage } from 'support/helpers/navigation/navigation';
import {
  DocumentType,
  DocumentTypeWithVirtual,
  PriceCatalogRoutes,
  RelationshipV2,
  TransactionInternalType,
} from 'support/types';
import { UnsavedChangesModal } from 'components/Navigation/UnsavedChangesModal/UnsavedChangesModal';
import { Page } from 'components/Page/Page';
import { OcrResult } from 'components/WebEDI/DocumentExtractor/types';
import { MessageFieldErrorModal } from 'components/WebEDI/MessageFieldErrorModal/MessageFieldErrorModal';

import { useCreateDocumentSetup } from './hooks/useCreateDocumentSetup';
import { CreateDocumentSteps, useCreateDocumentSteps } from './hooks/useCreateDocumentSteps';
import { useDocumentSubmission } from './hooks/useDocumentSubmission';
import { Datachecks } from '../Datachecks/Datachecks';
import { CreateDocumentHeader } from './components/CreateDocumentHeader/CreateDocumentHeader';
import { DocumentCreationStep } from './components/DocumentCreationStep/DocumentCreationStep';
import { PriceCatalogUploadModal } from './components/PriceCatalogUploadModal/PriceCatalogUploadModal';
import { SendConfirmationModal } from './components/SendConfirmationModal/SendConfirmationModal';

export const DocumentCreation = ({
  isProductCatalogRoute,
  documentType,
  relationship,
  sourceMessageId,
  virtualDocumentType,
}: {
  isProductCatalogRoute: boolean;
  relationship: RelationshipV2 | undefined;
  sourceMessageId: string | undefined;
  documentType: DocumentType | undefined;
  virtualDocumentType: DocumentTypeWithVirtual | undefined;
}) => {
  useInitWebEDIStore();
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const [ocrResult, setOcrResult] = useState<OcrResult>();
  const isLoadingWebEDIData = useWebEDIStore((state) => state.isLoading);

  const { resolver, isLoadingSetup, receiverProcessSpecification } = useCreateDocumentSetup({
    relationship,
    sourceMessageId,
    documentType,
  });

  const { state: step, isLoading: isLoadingStep, dispatch } = useCreateDocumentSteps({ documentType });

  const navigate = useNavigate();
  const { previousPath } = useHistoryStore();
  const handleCancel = useCallback(() => {
    navigate(
      previousPath
        ? previousPath
        : isProductCatalogRoute || !sourceMessageId
          ? PriceCatalogRoutes.root
          : routeToTransactionPage(TransactionInternalType.message, sourceMessageId),
    );
  }, [isProductCatalogRoute, navigate, previousPath, sourceMessageId]);

  const formMethods = useForm<Record<string, unknown>>({
    mode: 'onSubmit',
    resolver,
    reValidateMode: 'onChange',
  });

  const {
    handleSubmit,
    formState: { isSubmitting: isPreppingDocumentForSubmission },
  } = formMethods;

  const { submitDocument, isCreating, isSuccess } = useDocumentSubmission(
    relationship,
    documentType,
    ocrResult,
    isProductCatalogRoute,
  );

  const handleFormSubmit = () => {
    submitDocument(formMethods.getValues(), formMethods.formState.dirtyFields, setConfirmationModalOpen);
  };

  const handleOpenConfirmation = handleSubmit(() => {
    setConfirmationModalOpen(true);
  });

  const handleCancelConfirmation = () => {
    formMethods.reset(undefined, { keepValues: true, keepDirty: true, keepTouched: true, keepIsValid: true });
    setConfirmationModalOpen(false);
  };

  const [uploadedPricat, setUploadedPricat] = useState<string>();
  const handlePriceCatalogLoad = (data: Array<Record<string, unknown>>, filename: string) => {
    formMethods.setValue('line_items', data);
    setUploadedPricat(filename);
    dispatch({ type: 'GOTO_DOCUMENT_CREATION' });
  };

  if (isLoadingSetup || !relationship || !documentType || !virtualDocumentType) {
    return <Page isLoading={true}>{null}</Page>;
  }

  const isLoadingWebEDIInformation = isLoadingWebEDIData || isLoadingStep;

  return (
    <FormProvider {...formMethods}>
      <Datachecks targetRelationshipId={relationship.id} documentType={documentType} />
      <ErrorsNavigationContextProvider<WebEDIError>
        service={WebEDIErrorsServiceSingleton}
        ErrorModalComponent={MessageFieldErrorModal}
      >
        <Page isLoading={isLoadingWebEDIInformation}>
          <CreateDocumentHeader
            step={step}
            relationship={relationship}
            virtualDocumentType={virtualDocumentType}
            nonVirtualDocumentType={documentType}
            sourceMessageId={sourceMessageId}
            handleOpenConfirmation={handleOpenConfirmation}
            isCreatingDocument={isCreating}
            isPreppingDocumentForSubmission={isPreppingDocumentForSubmission}
            setOcrResult={setOcrResult}
            onCancel={handleCancel}
          />

          <DocumentCreationStep
            nonVirtualDocumentType={documentType}
            relationship={relationship}
            handleOpenConfirmation={handleOpenConfirmation}
            uploadedFileName={uploadedPricat}
            onUploadNewFile={() => dispatch({ type: 'GOTO_PRICAT_UPLOAD' })}
            isCreatingDocument={isCreating || isPreppingDocumentForSubmission}
          />

          <PriceCatalogUploadModal
            relationship={relationship}
            onLoad={handlePriceCatalogLoad}
            open={step === CreateDocumentSteps.PRICAT_UPLOAD}
            processSpec={receiverProcessSpecification}
            onClose={uploadedPricat ? () => dispatch({ type: 'GOTO_DOCUMENT_CREATION' }) : undefined}
            onCancel={handleCancel}
          />

          <SendConfirmationModal
            onConfirm={handleFormSubmit}
            open={confirmationModalOpen}
            onCancel={handleCancelConfirmation}
            documentType={virtualDocumentType}
            receiverTradePartner={relationship.receiver_partner}
          />

          <UnsavedChangesModal shouldBlock={!isSuccess} />
        </Page>
      </ErrorsNavigationContextProvider>
    </FormProvider>
  );
};
